import { H3 } from '@rikstv/shared-components';
import classNames from 'classnames';
import { type FC } from 'react';
import { Button } from '../../../../../../shared';
import { SelectedIcon } from '../selection-icon/SelectedIcon';
import styles from './BringYourOwnDevice.module.css';
import { useDialog } from '~/components/shared/dialog/useDialog';
import { DialogCloseButton } from '~/components/shared/dialog/dialog-close-button';

export const BringYourOwnDevice: React.FC<{ isBYODSelected: boolean; clickHandler: () => void }> = ({ isBYODSelected, clickHandler }) => {
  const dialog = useDialog();

  return (
    <div className={classNames(styles.byod, { [styles.selected]: isBYODSelected })}>
      {isBYODSelected && <SelectedIcon />}
      <div className={styles.content}>
        <H3 lookLike="sub-title-2">Bruk utstyret du allerede har</H3>
        <p>
          Har du en Apple TV, Chromecast eller Smart-TV? Da trenger du ikke kjøpe nytt utstyr. Se TV enkelt med det du allerede har hjemme.
        </p>
      </div>
      <div className={styles.buttons}>
        <Button type="primary" onClick={clickHandler} data-testid="toggle-byod">
          {isBYODSelected ? 'Endre' : 'Velg'}
        </Button>
        <Button type="tertiary" onClick={() => dialog.openModalDialog()} data-testid="open-byod-modal">
          Les mer
        </Button>
      </div>
      <ByodDialog onClickSelect={clickHandler} isSelected={isBYODSelected} dialogRef={dialog.ref} />
    </div>
  );
};

const ByodDialog: FC<{ onClickSelect: () => void; isSelected: boolean; dialogRef: React.RefObject<HTMLDialogElement | null> }> = ({
  onClickSelect,
  isSelected,
  dialogRef,
}) => {
  return (
    <dialog ref={dialogRef}>
      <div className={styles.modalFlexSection}>
        <h2 className={'title-3'}>Bruk ditt eget utstyr</h2>
        <section>
          <b>Hva betyr 'Bruk ditt eget utstyr'?</b> Du kan se TV uten å kjøpe ekstra utstyr hvis du har en kompatibel enhet og internett.
          Last ned RiksTV-appen på din Smart-TV, Apple TV eller Android-enhet. Når du har fullført bestillingen, kan du begynne å se TV med
          en gang på enheten din.
        </section>
        <section>
          <h3 className="title-4">Her kan du se RiksTV</h3>
          <div className={styles.modalFlexSection}>
            <div className={styles.modalGridSection}>
              <SmartTvSvg />
              <ul>
                <li>Samsung SMART-TV (Tizen), 2017 og nyere.</li>
                <li>LG SMART-TV (WebOS), 2018 og nyere.</li>
                <li>Philips SMART-TV med Android 7.0 og nyere.</li>
                <li>Sony SMART-TV med Android 7.0 og nyere.</li>
                <li>AndroidTV/GoogleTV 7.0 og nyere.</li>
                <li>AppleTV med tvOS 16.0 og nyere.</li>
              </ul>
            </div>
            <div className={styles.modalGridSection}>
              <MobileTabletSvg />
              <p>
                Du kan også laste ned RiksTV-appen på din smarttelefon eller nettbrett. Gå til App Store eller Google Play og søk etter
                RiksTV.
              </p>
            </div>
            <div className={styles.modalGridSection}>
              <Laptop />
              <p>
                Se RiksTV direkte i nettleseren på din datamaskin ved å besøke{' '}
                <b>
                  <code>play.rikstv.no</code>
                </b>
                .
              </p>
            </div>
          </div>
        </section>
        <section>
          <h3 className="button-or-bold">Er du fortsatt usikker på om dette gjelder deg?</h3>
          <p>
            Gå til der du laster ned apper (App Store eller Google Play) og søk etter RiksTV. Hvis du kan laste ned appen, er enheten din
            kompatibel.
          </p>
        </section>
        {!isSelected && (
          <div className={styles.modalSelectButtonWrapper}>
            <Button
              type="primary"
              onClick={() => {
                onClickSelect();
                dialogRef.current?.close();
              }}
            >
              {isSelected ? 'Endre' : 'Velg'}
            </Button>
          </div>
        )}

        <DialogCloseButton onClick={() => dialogRef.current?.close()} />
      </div>
    </dialog>
  );
};

const SmartTvSvg = () => (
  <svg viewBox="0 0 65 65" fill="none" xmlns="http://www.w3.org/2000/svg">
    <title>Smart TV</title>
    <path
      d="M13.6876 48.9598L9.02364 53.6288M51.9362 48.9598L56.6009 53.6288M4.29644 11.6035H60.3948C61.9058 11.6035 63.1309 12.718 63.1309 14.0939V46.4686C63.1309 47.8446 61.9058 48.959 60.3948 48.959H4.29572C2.78682 48.959 1.55957 47.8439 1.55957 46.4686V14.0939C1.55957 12.718 2.78753 11.6035 4.29644 11.6035Z"
      stroke="black"
      strokeWidth="2.0012"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
const MobileTabletSvg = () => (
  <svg viewBox="17 0 75 65" fill="none" xmlns="http://www.w3.org/2000/svg">
    <title>mobile or tablet</title>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M32.9275 48.9528C33.3508 48.9528 33.693 49.3017 33.693 49.7332Z"
      stroke="black"
      strokeWidth="2.0012"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M32.161 49.7329C32.161 49.3013 32.5043 48.9528 32.9275 48.9528Z"
      stroke="black"
      strokeWidth="2.0012"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M32.9274 50.5161C32.5041 50.5161 32.161 50.1675 32.161 49.7329Z"
      stroke="black"
      strokeWidth="2.0012"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M33.693 49.7332C33.693 50.1679 33.3507 50.5161 32.9274 50.5161Z"
      stroke="black"
      strokeWidth="2.0012"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M24.8179 7.88477H41.0276C44.6088 7.88477 47.5115 10.8444 47.5115 14.4927V50.8565C47.5115 54.5048 44.6088 57.4644 41.0276 57.4644H24.8179C21.2367 57.4644 18.334 54.5048 18.334 50.8565V14.4927C18.334 10.8444 21.2367 7.88477 24.8179 7.88477Z"
      stroke="black"
      strokeWidth="2.0012"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M71.1085 53.5005C71.4427 53.5005 71.7129 53.7974 71.7129 54.162C71.7129 54.5266 71.4427 54.8226 71.1085 54.8226M71.1054 53.5004C70.7712 53.5004 70.4993 53.7966 70.4993 54.1611C70.4993 54.5257 70.7714 54.8222 71.1056 54.8222M86.2874 58.5156C88.8869 58.5156 90.9928 56.4882 90.9928 53.9932L90.963 11.0068C90.963 8.50943 88.8571 6.48438 86.2577 6.48438H55.6741C53.0746 6.48438 50.9688 8.50943 50.9688 11.0068V53.9932C50.9688 56.4882 53.0746 58.5156 55.6741 58.5156H86.2874Z"
      stroke="black"
      strokeWidth="2.0012"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Laptop = () => (
  <svg viewBox="0 0 65 65" fill="none" xmlns="http://www.w3.org/2000/svg">
    <title>laptop</title>
    <path
      d="M7.94952 41.7681V18.9258C7.94952 17.3147 9.26841 16.0098 10.8967 16.0098H53.1403C54.7662 16.0098 56.0875 17.3147 56.0875 18.9258V41.7681M37.0958 42.0473H59.0354V42.5333C59.0354 45.7531 56.3952 48.3653 53.1409 48.3653H10.8974C7.6407 48.3653 5.00293 45.7531 5.00293 42.5333V42.0473H26.9401L26.9303 42.0254V43.0776C26.9303 43.4518 27.2373 43.7556 27.618 43.7556H36.3246C36.7052 43.7556 37.0122 43.4518 37.0122 43.0776V42.0254L37.0958 42.0473Z"
      stroke="black"
      strokeWidth="2.0012"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);
